import React, { useState, useMemo, useEffect, useCallback } from "react";
import ExcelJS from "exceljs";
import ReusableTable from "../components/Table/ReusableTable";
import ProductMapping from "./ProductMapping";
import CustomDialog from "../components/dialog/CustomDialog";
import { get, post } from "../utils/api";
import {
  Search, Download, ArrowLeft, Loader2, Eye
} from "lucide-react";

const sanitizeSheetName = (sheetName, fallbackName) => {
  const cleanedName = (sheetName || fallbackName || "Sheet")
    .replace(/[\\/*?:[\]]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return (cleanedName || fallbackName || "Sheet").slice(0, 31);
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return `${value}`;
};

const formatPercentValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return `${value}%`;
};

const applyCellBorder = (cell) => {
  cell.border = {
    top: { style: "thin", color: { argb: "FFCBD5E1" } },
    left: { style: "thin", color: { argb: "FFCBD5E1" } },
    bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
    right: { style: "thin", color: { argb: "FFCBD5E1" } },
  };
};

const styleTitleRow = (worksheet, rowNumber, totalColumns) => {
  for (let col = 1; col <= totalColumns; col += 1) {
    const cell = worksheet.getCell(rowNumber, col);
    applyCellBorder(cell);
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE0E7FF" },
    };
  }

  const titleCell = worksheet.getCell(rowNumber, 1);
  titleCell.font = {
    name: "Calibri",
    size: 13,
    bold: true,
    color: { argb: "FF312E81" },
  };
  titleCell.alignment = {
    horizontal: "center",
    vertical: "middle",
  };
};

const styleHeaderRow = (worksheet, rowNumber, totalColumns) => {
  for (let col = 1; col <= totalColumns; col += 1) {
    const cell = worksheet.getCell(rowNumber, col);
    applyCellBorder(cell);
    cell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4F46E5" },
    };
  }
};

const styleDataRow = (worksheet, rowNumber, totalColumns) => {
  for (let col = 1; col <= totalColumns; col += 1) {
    const cell = worksheet.getCell(rowNumber, col);
    applyCellBorder(cell);
    cell.font = {
      name: "Calibri",
      size: 11,
      color: { argb: "FF1E293B" },
    };
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
  }
};

const createProduct1Rows = (rows) =>
  rows.map((row) => [
    formatCellValue(row.class_code),
    formatCellValue(row.size),
    formatPercentValue(row.management_fee_sharing_per),
    formatPercentValue(
      row.placement_fee_per ?? row.performance_fee_sharing_per ?? row.total_fee_per
    ),
    formatPercentValue(row.upfront_fee_per),
    formatPercentValue(row.trail_fee_per),
    formatPercentValue(row.total_fee_per ?? row.placement_fee_per),
  ]);

const createProduct2Rows = (rows) => {
  const moreThan15CrRow = rows.find(
    (row) =>
      row.management_fee_sharing_per_morethan15cr !== null &&
      row.management_fee_sharing_per_morethan15cr !== undefined &&
      row.management_fee_sharing_per_morethan15cr !== ""
  );

  const lessThan15CrRow =
    rows.find(
      (row) =>
        row.management_fee_sharing_per !== null &&
        row.management_fee_sharing_per !== undefined &&
        row.management_fee_sharing_per !== ""
    ) || rows[0];

  const resultRows = [];

  if (lessThan15CrRow) {
    resultRows.push([
      formatCellValue(lessThan15CrRow.class_code),
      "Less than 15 Cr",
      formatPercentValue(lessThan15CrRow.management_fee_sharing_per),
      formatPercentValue(lessThan15CrRow.performance_fee_sharing_per),
    ]);
  }

  if (moreThan15CrRow) {
    resultRows.push([
      formatCellValue(moreThan15CrRow.class_code),
      "More than 15 Cr",
      formatPercentValue(moreThan15CrRow.management_fee_sharing_per_morethan15cr),
      formatPercentValue(moreThan15CrRow.performance_fee_sharing_per),
    ]);
  }

  return resultRows.length > 0 ? resultRows : [["-", "-", "-", "-"]];
};

const ProductMapped = () => {
  const [showProductMapping, setShowProductMapping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [commercialDialogVisible, setCommercialDialogVisible] = useState(false);
  const [commercialLoading, setCommercialLoading] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [commercialDetails, setCommercialDetails] = useState([]);

  const openCommercialDetails = useCallback(async (row) => {
    setSelectedDistributor(row);
    setCommercialDialogVisible(true);
    setCommercialLoading(true);

    try {
      const response = await post(
        "/distributorproductmapping/fetchDistributorCommercialDetails",
        {
          distributor_sequence_number: row.distributor_sequence_number,
        }
      );

      if (response.data?.Success) {
        setCommercialDetails(response.data.Data || []);
      } else {
        setCommercialDetails([]);
      }
    } catch (error) {
      console.error("Error fetching distributor commercial details:", error);
      setCommercialDetails([]);
    } finally {
      setCommercialLoading(false);
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "DISTRIBUTOR NAME",
        accessor: "distributor_name",
        Cell: ({ value }) => <span className="font-extrabold text-[#1E293B] uppercase">{value}</span>
      },
      {
        Header: "FUND CODES",
        accessor: "fund_codes",
        Cell: ({ value }) => (
          <div className="flex items-center justify-center text-gray-600 font-medium uppercase">
            {value || "-"}
          </div>
        )
      },
      {
        Header: "PRODUCT",
        accessor: "class_codes",
        Cell: ({ value }) => (
          <div className="flex items-center justify-center text-gray-600 font-medium uppercase">
            {value || "-"}
          </div>
        )
      },
      {
        Header: "COMMERCIALS",
        accessor: "action",
        Cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() => openCommercialDetails(row.original)}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 transition-all hover:bg-indigo-600 hover:text-white"
              title="View commercial details"
            >
              <Eye className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    [openCommercialDetails]
  );

  const isProduct2 = useMemo(
    () =>
      commercialDetails.length > 0 &&
      (Number(commercialDetails[0]?.fund_product_id) === 2 ||
        commercialDetails.some(
          (row) =>
            row.management_fee_sharing_per_morethan15cr !== null &&
            row.management_fee_sharing_per_morethan15cr !== undefined &&
            row.management_fee_sharing_per_morethan15cr !== ""
        )),
    [commercialDetails]
  );

  const commercialTableData = useMemo(() => {
    if (!isProduct2) return commercialDetails;

    const result = [];
    commercialDetails.forEach((row) => {
      const hasLess =
        row.management_fee_sharing_per !== null &&
        row.management_fee_sharing_per !== undefined &&
        row.management_fee_sharing_per !== "";
      const hasMore =
        row.management_fee_sharing_per_morethan15cr !== null &&
        row.management_fee_sharing_per_morethan15cr !== undefined &&
        row.management_fee_sharing_per_morethan15cr !== "";

      if (hasLess) {
        result.push({
          ...row,
          _capital_commitment: "Less than 15 Cr",
          _mgmt_fee: row.management_fee_sharing_per,
        });
      }
      if (hasMore) {
        result.push({
          ...row,
          _capital_commitment: "More than 15 Cr",
          _mgmt_fee: row.management_fee_sharing_per_morethan15cr,
        });
      }
    });

    return result.length > 0 ? result : commercialDetails;
  }, [commercialDetails, isProduct2]);

  const commercialColumns = useMemo(() => {
    if (isProduct2) {
      return [
        { Header: "FUND CODE", accessor: "fund_code" },
        { Header: "CLASS CODE", accessor: "class_code" },
        { Header: "CAPITAL COMMITMENT", accessor: "_capital_commitment" },
        {
          Header: "MANAGEMENT FEES P.A.",
          accessor: "_mgmt_fee",
          Cell: ({ value }) => (value !== null && value !== undefined && value !== "" ? `${value}%` : "-"),
        },
        {
          Header: "DISTRIBUTOR SHARING IN MONTHLY FEES",
          accessor: "performance_fee_sharing_per",
          Cell: ({ value }) => (value !== null && value !== undefined && value !== "" ? `${value}%` : "-"),
        },
      ];
    }

    return [
      { Header: "FUND CODE", accessor: "fund_code" },
      { Header: "CLASS CODE", accessor: "class_code" },
      { Header: "CONTRIBUTION (IN CR)", accessor: "size" },
      { Header: "M. FEES", accessor: "management_fee_sharing_per" },
      { Header: "TOTAL DISTRIBUTION FEES", accessor: "placement_fee_per" },
      { Header: "UPFRONT", accessor: "upfront_fee_per" },
      { Header: "TRAIL", accessor: "trail_fee_per" },
      {
        Header: "TOTAL FEE",
        accessor: "total_fee_per",
        Cell: ({ row }) => row.original.placement_fee_per ?? "-",
      },
    ];
  }, [isProduct2]);

  const fetchDistributorProductMappings = async () => {
    setLoading(true);
    try {
      const userDetails = JSON.parse(localStorage.getItem("user_details") || "{}");
      const email = userDetails?.email;

      const response = await post("/distributorproductmapping/fetchProductMappedForDistributor", { email });
      if (response.data.Success) {
        setTableData(response.data.Data);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching product mapped data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistributorProductMappings();
  }, []);

  const filteredData = useMemo(() => {
    if (!searchTerm) return tableData;
    const lower = searchTerm.toLowerCase();
    return tableData.filter(item =>
      Object.values(item).some(val =>
        val != null && val.toString().toLowerCase().includes(lower)
      )
    );
  }, [tableData, searchTerm]);

  const downloadExcel = useCallback(async () => {
    try {
      const response = await get(
        "/distributorproductmapping/fetchDistributorCommercialExportData"
      );

      const exportRows = response.data?.Data || [];

      if (!response.data?.Success || exportRows.length === 0) {
        alert("No data available to export");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Codex";
      workbook.created = new Date();
      workbook.modified = new Date();

      const groupedByFund = exportRows.reduce((accumulator, row) => {
        const fundKey = `${row.fund_id}`;

        if (!accumulator[fundKey]) {
          accumulator[fundKey] = {
            fund_name: row.fund_name,
            product_id: Number(row.fund_product_id),
            rows: [],
          };
        }

        accumulator[fundKey].rows.push(row);
        return accumulator;
      }, {});

      Object.values(groupedByFund).forEach((fundGroup, fundIndex) => {
        const isP2 = Number(fundGroup.product_id) === 2;
        const headers = isP2
          ? [
              "Class",
              "Capital Commitment",
              "Management Fees p.a.",
              "Distributor Sharing in Monthly Fees",
            ]
          : [
              "Class",
              "Contribution (In CR)",
              "M. Fees",
              "Total Distribution Fees",
              "Upfront",
              "Trail",
              "Total Fee",
            ];

        const worksheet = workbook.addWorksheet(
          sanitizeSheetName(fundGroup.fund_name, `Fund ${fundIndex + 1}`)
        );

        worksheet.properties.defaultRowHeight = 22;
        worksheet.columns = headers.map((header) => ({
          width: Math.max(header.length + 6, 18),
        }));

        const distributorGroups = fundGroup.rows.reduce((accumulator, row) => {
          const distributorKey = row.distributor_sequence_number || row.distributor_name;

          if (!accumulator[distributorKey]) {
            accumulator[distributorKey] = {
              distributor_name: row.distributor_name,
              rows: [],
            };
          }

          accumulator[distributorKey].rows.push(row);
          return accumulator;
        }, {});

        Object.values(distributorGroups).forEach((distributorGroup) => {
          const titleRowNumber = worksheet.rowCount + 1;
          worksheet.mergeCells(titleRowNumber, 1, titleRowNumber, headers.length);
          worksheet.getCell(titleRowNumber, 1).value = `${formatCellValue(
            distributorGroup.distributor_name
          )} - ${formatCellValue(fundGroup.fund_name)}`;
          styleTitleRow(worksheet, titleRowNumber, headers.length);

          const headerRowNumber = worksheet.rowCount + 1;
          worksheet.addRow(headers);
          styleHeaderRow(worksheet, headerRowNumber, headers.length);

          const detailRows = isP2
            ? createProduct2Rows(distributorGroup.rows)
            : createProduct1Rows(distributorGroup.rows);

          detailRows.forEach((detailRow) => {
            const dataRowNumber = worksheet.rowCount + 1;
            worksheet.addRow(detailRow);
            styleDataRow(worksheet, dataRowNumber, headers.length);
          });

          worksheet.addRow(new Array(headers.length).fill(""));
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      const blobUrl = URL.createObjectURL(blob);
      link.href = blobUrl;
      link.download = `product_mapped_details_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error exporting product mapped workbook:", error);
      alert("Failed to export Excel file");
    }
  }, []);

  const handleBackAndRefresh = async () => {
    await fetchDistributorProductMappings();
    setShowProductMapping(false);
  };

  return (
    <div className="w-full max-w-screen-xl mx-auto p-6 h-full min-h-screen bg-[#FBFBFE] space-y-8 animate-in fade-in duration-700">
      {!showProductMapping ? (
        <>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="relative flex items-center">
              <Search className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name, PAN, APRN, fund, product…"
                className="pl-9 pr-4 h-[40px] w-72 rounded-xl border border-gray-200 bg-white text-xs text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={downloadExcel}
                className="flex items-center justify-center gap-2 text-white bg-[#6366F1] hover:bg-[#4F46E5] h-[40px] px-4 rounded-xl font-semibold shadow-lg shadow-indigo-100 border-none transition-all active:scale-95"
              >
                <Download className="w-4 h-4" />
                <span className="text-xs">Download to Excel</span>
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center gap-6">
          <button
            onClick={handleBackAndRefresh}
            className="w-10 h-10 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center hover:bg-gray-50 transition-all active:scale-95 group"
          >
            <ArrowLeft size={20} className="text-gray-400 group-hover:text-indigo-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800 tracking-tight">Configuration Hub</h1>
            <p className="text-sm font-medium text-gray-400">Map Distributor to specific Fund Products</p>
          </div>
        </div>
      )}

      <div className="rounded-xl overflow-hidden">
        {showProductMapping ? (
          <div className="p-6">
            <ProductMapping onBack={handleBackAndRefresh} />
          </div>
        ) : (
          <div>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4 text-gray-400">
                <Loader2 size={32} className="animate-spin text-indigo-500" />
                <span className="text-xs font-bold tracking-widest uppercase">Loading data...</span>
              </div>
            ) : filteredData.length > 0 ? (
              <div className="overflow-x-auto custom-mapping-table">
                <style>{`
                  .custom-mapping-table thead {
                    background-color: #F8FAFC !important;
                  }
                  .custom-mapping-table th {
                    color: #6366F1 !important;
                    font-size: 10px !important;
                    font-weight: 800 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    padding: 16px 12px !important;
                    border-bottom: 1px solid #F1F5F9 !important;
                    text-align: center !important;
                  }
                  .custom-mapping-table th div {
                    justify-content: center !important;
                  }
                  .custom-mapping-table td {
                    padding: 16px 12px;
                    vertical-align: middle;
                    font-size: 11px;
                    color: #1E293B;
                    text-align: center !important;
                  }
                `}</style>
                <ReusableTable columns={columns} data={filteredData} />
              </div>
            ) : (
              <div className="text-center py-20 text-gray-500 font-medium italic">
                No mapping records found.
              </div>
            )}
          </div>
        )}
      </div>

      <CustomDialog
        visible={commercialDialogVisible}
        onHide={() => {
          setCommercialDialogVisible(false);
          setCommercialDetails([]);
          setSelectedDistributor(null);
        }}
        header={`Commercial Details${selectedDistributor?.distributor_name ? ` - ${selectedDistributor.distributor_name}` : ""}`}
        width="80vw"
      >
        <div className="space-y-4">
          {selectedDistributor && (
            <div className="grid grid-cols-1 gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-700 md:grid-cols-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Distributor</p>
                <p className="font-semibold uppercase">{selectedDistributor.distributor_name}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">PAN</p>
                <p className="font-semibold uppercase">{selectedDistributor.pan_number || "-"}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">APRN</p>
                <p className="font-semibold uppercase">{selectedDistributor.arn_code || "-"}</p>
              </div>
            </div>
          )}

          {commercialLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-gray-400">
              <Loader2 size={28} className="animate-spin text-indigo-500" />
              <span className="text-xs font-bold tracking-widest uppercase">Loading commercials...</span>
            </div>
          ) : commercialDetails.length > 0 ? (
            <div className="overflow-x-auto">
              <ReusableTable columns={commercialColumns} data={commercialTableData} />
            </div>
          ) : (
            <div className="py-12 text-center text-sm font-medium text-gray-500">
              No commercial details found for this distributor.
            </div>
          )}
        </div>
      </CustomDialog>
    </div>
  );
};

export default ProductMapped;
